import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  TextInput,
  FlatList,
  Modal,
} from 'react-native';
import SvgIcon from '../components/SvgIcon';
import { useNavigation, useRoute } from '@react-navigation/native';
import colors from '../theme/colors';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const ShareProfileScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { memberId } = route.params;
  const { user } = useAuth();
  
  const [memberProfile, setMemberProfile] = useState(null);
  const [compatibleProfiles, setCompatibleProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [sharingProfileId, setSharingProfileId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [dialog, setDialog] = useState({ visible: false, type: '', title: '', message: '', onConfirm: null });
  const [showShareOptionsModal, setShowShareOptionsModal] = useState(false);
  const [selectedProfileToShare, setSelectedProfileToShare] = useState(null);
  const [selectedProfileShares, setSelectedProfileShares] = useState([]);

  // Icon URLs for bride and groom
const BRIDE_ICON = 'https://kalyanamitra.in/assets/ps-bride-UWGtbgl1.png';
const GROOM_ICON = 'https://kalyanamitra.in/assets/ps-groom-D-L2Y5H0.png';

  const showDialog = (type, title, message, onConfirm = null) => {
    setDialog({ visible: true, type, title, message, onConfirm });
  };

  const hideDialog = () => {
    setDialog({ visible: false, type: '', title: '', message: '', onConfirm: null });
  };

  const handleDialogConfirm = () => {
    if (dialog.onConfirm) {
      dialog.onConfirm();
    }
    hideDialog();
  };

  useEffect(() => {
    loadMemberProfile();
  }, [memberId]);

  useEffect(() => {
    if (memberProfile) {
      loadCompatibleProfiles();
    }
  }, [memberProfile]);

  const loadMemberProfile = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/EnhancedMember/shortProfile/${memberId}?includePhotos=true`);
      const profileData = response.data?.data || response.data;
      console.log('Member Profile:', profileData);
      setMemberProfile(profileData);
    } catch (error) {
      console.error('Error loading member profile:', error);
      showDialog('error', 'Error', 'Failed to load member profile', () => navigation.goBack());
    } finally {
      setIsLoading(false);
    }
  };

  const loadCompatibleProfiles = async () => {
    setIsLoadingProfiles(true);
    try {
      if (!memberProfile) {
        console.log('Member profile not loaded yet');
        return;
      }

      // Determine opposite gender
      const oppositeGender = memberProfile?.gender === 'Male' ? 'Female' : 'Male';
      const memberType = oppositeGender === 'Female' ? 'Bride' : 'Groom';
      
      const requestData = {
        filterOptions: ["highMatch"],
        mediatorId: user?.mediatorId,
        memberType: memberType,
        memberId: memberId,
        pageSize: 10,
        pageNumber: 1,
      };

      if (searchQuery && searchQuery.trim()) {
        requestData.searchTerm = searchQuery.trim();
      }

      console.log('Loading compatible profiles with payload:', requestData);
      const response = await api.post('/api/EnhancedMember/list', requestData);
      
      console.log('API Response:', response.data);
      
      if (response.data && response.data.members) {
        // Filter out the current member and map to UI format
        const profiles = response.data.members
          .filter(m => m.memberId !== memberId)
          .map(member => ({
            id: member.memberId,
            name: `${member.name} ${member.surName}`,
            age: member.age,
            location: member.city || member.state || 'Location not specified',
            profession: member.profession || 'Not specified',
            education: member.highestEducationQualification || 'Not specified',
            image: member.photos && member.photos.length > 0 
              ? member.photos[0].securedUrl 
              : (member.gender === 'Male' ? GROOM_ICON : BRIDE_ICON),
            verified: member.isApproved,
            matchPercentage: member.matchPercentage || calculateMatchPercentage(member),
            pendingApproval: !member.isApproved,
            memberType: member.gender === 'Male' ? 'Groom' : 'Bride',
            shares: member.shares || [],
          }));
        
        console.log('Mapped profiles count:', profiles.length);
        setCompatibleProfiles(profiles);
      } else {
        console.log('No members in response');
        setCompatibleProfiles([]);
      }
    } catch (error) {
      console.error('Error loading compatible profiles:', error);
      console.error('Error details:', error.response?.data);
      showDialog('error', 'Error', error.response?.data?.message || 'Failed to load compatible profiles');
    } finally {
      setIsLoadingProfiles(false);
    }
  };

  const calculateMatchPercentage = (profile) => {
    // Simple match calculation - can be enhanced based on preferences
    let score = 0;
    let total = 0;

    // Age proximity (if within 5 years)
    if (memberProfile?.age && profile.age) {
      total += 25;
      const ageDiff = Math.abs(memberProfile.age - profile.age);
      if (ageDiff <= 5) score += 25 - (ageDiff * 3);
    }

    // Location match
    if (memberProfile?.state && profile.state) {
      total += 25;
      if (memberProfile.state === profile.state) score += 25;
    }

    // Education level
    if (memberProfile?.highestEducationQualification && profile.highestEducationQualification) {
      total += 25;
      score += 15; // Base score for having education
    }

    // Profession
    if (memberProfile?.profession && profile.profession) {
      total += 25;
      score += 15; // Base score for having profession
    }

    return total > 0 ? Math.round((score / total) * 100) : 50;
  };

  const handleSearch = () => {
    loadCompatibleProfiles();
  };

  const openShareOptionsModal = (profileId, shares = []) => {
    setSelectedProfileToShare(profileId);
    setSelectedProfileShares(shares);
    setShowShareOptionsModal(true);
  };

  const closeShareOptionsModal = () => {
    setShowShareOptionsModal(false);
    setSelectedProfileToShare(null);
    setSelectedProfileShares([]);
  };

  const handleShareWithOption = async (shareType) => {
    if (!selectedProfileToShare || sharingProfileId) return;
    
    closeShareOptionsModal();
    setSharingProfileId(selectedProfileToShare);
    
    try {
      if (shareType === 'Both') {
        // Make two API calls for both directions
        const payloadBrideToGroom = {
          sharedFromMemberId: memberId,
          sharedToMemberId: selectedProfileToShare,
          sharedByMediatorId: user?.mediatorId,
          shareType: 'BrideToGroom'
        };
        const payloadGroomToBride = {
          sharedFromMemberId: memberId,
          sharedToMemberId: selectedProfileToShare,
          sharedByMediatorId: user?.mediatorId,
          shareType: 'GroomToBride'
        };
        
        console.log('Sharing profile with both:', payloadBrideToGroom, payloadGroomToBride);
        await Promise.all([
          api.post('/api/Share/profile', payloadBrideToGroom),
          api.post('/api/Share/profile', payloadGroomToBride)
        ]);
      } else {
        const payload = {
          sharedFromMemberId: memberId,
          sharedToMemberId: selectedProfileToShare,
          sharedByMediatorId: user?.mediatorId,
          shareType: shareType
        };
        console.log('Sharing profile:', payload);
        await api.post('/api/Share/profile', payload);
      }
      
      showDialog('success', 'Success', 'Profile shared successfully');
      // Reload profiles to update share status
      await loadCompatibleProfiles();
    } catch (error) {
      console.error('Error sharing profile:', error);
      showDialog('error', 'Error', error.response?.data?.message || 'Failed to share profile');
    } finally {
      setSharingProfileId(null);
    }
  };

  const renderShareResponseIcon = (share, allShares) => {
    const isBrideToGroom = share.shareType === 'BrideToGroom';
    const iconUrl = isBrideToGroom 
      ? GROOM_ICON
      : BRIDE_ICON;
    
    // Find active response from responses array
    const responses = share.responses || [];
    const activeResponse = responses.find(r => r.isActive === true);
    
    let responseIcon = null;
    let responseColor = colors.textSecondary;
    
    if (activeResponse) {
      if (activeResponse.responseType === 'NotInterested') {
        responseIcon = 'thumbs-down';
        responseColor = '#FF4444';
      } else if (activeResponse.responseType === 'Interested') {
        responseIcon = 'thumbs-up';
        responseColor = '#00D9B5';
      }
    }
    
    return (
      <View style={styles.shareIconContainer} key={share.shareId}>
        <Image source={{ uri: iconUrl }} style={styles.shareTypeIcon} />
        {responseIcon ? (
          <View style={[styles.responseIndicator, { backgroundColor: responseColor }]}>
            <SvgIcon name={responseIcon} size={10} color={colors.white} />
          </View>
        ) : (
          <View style={[styles.responseIndicator, { backgroundColor: '#FFA500' }]}>
            <SvgIcon name="hourglass-outline" size={10} color={colors.white} />
          </View>
        )}
      </View>
    );
  };

  const renderProfileCard = ({ item }) => {
    // Check if shared in both directions
    const isGroomToBrideShared = item.shares && item.shares.some(s => s.shareType === 'GroomToBride');
    const isBrideToGroomShared = item.shares && item.shares.some(s => s.shareType === 'BrideToGroom');
    const isFullyShared = isGroomToBrideShared && isBrideToGroomShared;
    const isPartiallyShared = item.shares && item.shares.length > 0;
    const isCurrentlySharing = sharingProfileId === item.id;
    
    // Check if current member (being shared from) is not approved
    const isCurrentMemberNotApproved = !memberProfile?.isApproved;
    
    return (
      <TouchableOpacity 
        style={styles.profileCard}
        onPress={() => navigation.push('ProfileDetail', { memberId: item.id })}
        activeOpacity={0.7}
      >
        <View style={styles.cardBody}>
          {/* Left 50% - Photo */}
          <View style={styles.photoSection}>
            <Image source={{ uri: item.image }} style={styles.profileImage} />
            
            {/* Share Icon in top-left */}
            <TouchableOpacity 
              style={[
                styles.shareIconButton, 
                isFullyShared && styles.shareIconButtonShared,
                isPartiallyShared && !isFullyShared && styles.shareIconButtonPartial,
                (item.pendingApproval || isCurrentMemberNotApproved) && styles.shareIconButtonPending
              ]}
              onPress={(e) => {
                e.stopPropagation();
                console.log('Share icon clicked. Target Pending:', item.pendingApproval, 'Current Member Approved:', memberProfile?.isApproved, 'FullyShared:', isFullyShared);
                if (isCurrentMemberNotApproved) {
                  showDialog(
                    'error',
                    'Cannot Share', 
                    'Cannot share this profile. The current member profile is pending for approval'
                  );
                } else if (item.pendingApproval) {
                  showDialog(
                    'error',
                    'Cannot Share', 
                    'Cannot share profile. This profile is pending for approval'
                  );
                } else if (!isCurrentlySharing) {
                  openShareOptionsModal(item.id, item.shares || []);
                }
              }}
              disabled={isCurrentlySharing}
            >
              {isCurrentlySharing ? (
                <ActivityIndicator size="small" color={colors.white} />
              ) : (
                <SvgIcon 
                  name={isFullyShared ? "checkmark-done" : (isPartiallyShared ? "checkmark" : "share-outline")} 
                  size={20} 
                  color={colors.white} 
                />
              )}
            </TouchableOpacity>
            
            {item.pendingApproval && (
              <View style={styles.pendingBanner}>
                <SvgIcon name="time-outline" size={12} color={colors.white} />
                <Text style={styles.pendingText}>PENDING</Text>
              </View>
            )}
            
            {item.matchPercentage && (
              <View style={styles.matchBadge}>
                <Text style={styles.matchText}>{item.matchPercentage}%</Text>
              </View>
            )}
            
            {/* Share Response Icons */}
            {item.shares && item.shares.length > 0 && (
              <View style={styles.shareResponseContainer}>
                {item.shares.map(share => renderShareResponseIcon(share, item.shares))}
              </View>
            )}
          </View>

          {/* Right 50% - Details */}
          <View style={styles.detailsSection}>
            <View style={styles.nameContainer}>
              <Text style={styles.profileName} numberOfLines={2}>{item.name}</Text>
            </View>
            
            <View style={styles.infoRow}>
              <SvgIcon name="calendar-outline" size={16} color={colors.accent} />
              <Text style={styles.infoText}>{item.age} years</Text>
            </View>
            
            <View style={styles.infoRow}>
              <SvgIcon name="location-outline" size={16} color={colors.accent} />
              <Text style={styles.infoText} numberOfLines={1}>{item.location}</Text>
            </View>
            
            <View style={styles.infoRow}>
              <SvgIcon name="briefcase-outline" size={16} color={colors.accent} />
              <Text style={styles.infoText} numberOfLines={1}>{item.profession}</Text>
            </View>
            
            <View style={styles.infoRow}>
              <SvgIcon name="school-outline" size={16} color={colors.accent} />
              <Text style={styles.infoText} numberOfLines={1}>{item.education}</Text>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.secondary} />
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Compact Header with Member Info and Search */}
      <View style={styles.compactHeader}>
        <View style={styles.headerRow}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <SvgIcon name="arrow-back" size={24} color={colors.white} />
          </TouchableOpacity>
          
          {!showSearchBar ? (
            <>
              <View style={styles.memberInfoCompact}>
                <View style={styles.memberAvatarSmall}>
                  <Text style={styles.memberInitialsSmall}>
                    {memberProfile?.name?.charAt(0)}{memberProfile?.surName?.charAt(0)}
                  </Text>
                </View>
                <View style={styles.memberTextCompact}>
                  <Text style={styles.headerTitleCompact}>Share Profile</Text>
                  <Text style={styles.memberNameSmall}>
                    {memberProfile?.name} {memberProfile?.surName} • {memberProfile?.age}y
                  </Text>
                </View>
              </View>
              
              <TouchableOpacity 
                onPress={() => setShowSearchBar(true)} 
                style={styles.iconButton}
              >
                <SvgIcon name="search" size={22} color={colors.white} />
              </TouchableOpacity>
            </>
          ) : (
            <>
              <TextInput
                style={styles.searchInputCompact}
                placeholder="Search profiles..."
                placeholderTextColor={colors.textSecondary}
                value={searchQuery}
                onChangeText={setSearchQuery}
                onSubmitEditing={handleSearch}
                autoFocus
              />
              <TouchableOpacity 
                onPress={() => {
                  setShowSearchBar(false);
                  setSearchQuery('');
                  loadCompatibleProfiles();
                }} 
                style={styles.iconButton}
              >
                <SvgIcon name="close" size={22} color={colors.white} />
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>

      {/* Results Header */}
      <View style={styles.resultsHeader}>
        <Text style={styles.resultsText}>
          Showing {compatibleProfiles.length} {compatibleProfiles.length === 1 ? 'profile' : 'profiles'}
        </Text>
      </View>

      {/* Compatible Profiles List */}
      {isLoadingProfiles ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.secondary} />
        </View>
      ) : (
        <FlatList
          data={compatibleProfiles}
          keyExtractor={(item) => item.id.toString()}
          renderItem={renderProfileCard}
          contentContainerStyle={styles.profilesList}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <SvgIcon name="people-outline" size={64} color={colors.textSecondary} />
              <Text style={styles.emptyText}>No compatible profiles found</Text>
            </View>
          }
        />
      )}

      {/* Share Options Modal */}
      <Modal visible={showShareOptionsModal} transparent animationType="slide">
        <TouchableOpacity 
          style={styles.shareOptionsOverlay} 
          activeOpacity={1} 
          onPress={closeShareOptionsModal}
        >
          <View style={styles.shareOptionsContainer}>
            <View style={styles.shareOptionsHeader}>
              <Text style={styles.shareOptionsTitle}>Share Profile With</Text>
              <TouchableOpacity onPress={closeShareOptionsModal} style={styles.closeButton}>
                <SvgIcon name="close" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            
            {(() => {
              // Check which share types are already done
              const isGroomToBrideShared = selectedProfileShares.some(s => s.shareType === 'GroomToBride');
              const isBrideToGroomShared = selectedProfileShares.some(s => s.shareType === 'BrideToGroom');
              const isBothShared = isGroomToBrideShared && isBrideToGroomShared;
              
              return (
                <View style={styles.shareOptionsBody}>
                  {/* Share with Bride */}
                  <TouchableOpacity 
                    style={[
                      styles.shareOptionItem,
                      isGroomToBrideShared && styles.shareOptionItemDone
                    ]}
                    onPress={() => !isGroomToBrideShared && handleShareWithOption('GroomToBride')}
                    disabled={isGroomToBrideShared}
                    activeOpacity={isGroomToBrideShared ? 1 : 0.7}
                  >
                    <View style={styles.shareOptionContent}>
                      <Image source={{ uri: GROOM_ICON }} style={styles.shareOptionIcon} />
                      <SvgIcon name="arrow-forward" size={20} color={isGroomToBrideShared ? colors.success : colors.secondary} style={styles.arrowIcon} />
                      <Image source={{ uri: BRIDE_ICON }} style={styles.shareOptionIcon} />
                    </View>
                    <View style={styles.shareOptionLabelRow}>
                      <Text style={[styles.shareOptionLabel, isGroomToBrideShared && styles.shareOptionLabelDone]}>Share with Bride</Text>
                      {isGroomToBrideShared && (
                        <View style={styles.sharedBadge}>
                          <SvgIcon name="checkmark-circle" size={16} color={colors.success} />
                          <Text style={styles.sharedBadgeText}>Shared</Text>
                        </View>
                      )}
                    </View>
                    <Text style={styles.shareOptionDesc}>Groom's profile will be shared with Bride</Text>
                  </TouchableOpacity>

                  {/* Share with Groom */}
                  <TouchableOpacity 
                    style={[
                      styles.shareOptionItem,
                      isBrideToGroomShared && styles.shareOptionItemDone
                    ]}
                    onPress={() => !isBrideToGroomShared && handleShareWithOption('BrideToGroom')}
                    disabled={isBrideToGroomShared}
                    activeOpacity={isBrideToGroomShared ? 1 : 0.7}
                  >
                    <View style={styles.shareOptionContent}>
                      <Image source={{ uri: BRIDE_ICON }} style={styles.shareOptionIcon} />
                      <SvgIcon name="arrow-forward" size={20} color={isBrideToGroomShared ? colors.success : colors.secondary} style={styles.arrowIcon} />
                      <Image source={{ uri: GROOM_ICON }} style={styles.shareOptionIcon} />
                    </View>
                    <View style={styles.shareOptionLabelRow}>
                      <Text style={[styles.shareOptionLabel, isBrideToGroomShared && styles.shareOptionLabelDone]}>Share with Groom</Text>
                      {isBrideToGroomShared && (
                        <View style={styles.sharedBadge}>
                          <SvgIcon name="checkmark-circle" size={16} color={colors.success} />
                          <Text style={styles.sharedBadgeText}>Shared</Text>
                        </View>
                      )}
                    </View>
                    <Text style={styles.shareOptionDesc}>Bride's profile will be shared with Groom</Text>
                  </TouchableOpacity>

                  {/* Share with Both */}
                  <TouchableOpacity 
                    style={[
                      styles.shareOptionItem, 
                      isBothShared && styles.shareOptionItemDone
                    ]}
                    onPress={() => !isBothShared && handleShareWithOption('Both')}
                    disabled={isBothShared}
                    activeOpacity={isBothShared ? 1 : 0.7}
                  >
                    <View style={styles.shareOptionContent}>
                      <View style={styles.bothIconsContainer}>
                        <Image source={{ uri: GROOM_ICON }} style={styles.shareOptionIconSmall} />
                        <Image source={{ uri: BRIDE_ICON }} style={styles.shareOptionIconSmall} />
                      </View>
                      <SvgIcon name="swap-horizontal" size={24} color={isBothShared ? colors.success : colors.secondary} style={styles.arrowIcon} />
                      <View style={styles.bothIconsContainer}>
                        <Image source={{ uri: BRIDE_ICON }} style={styles.shareOptionIconSmall} />
                        <Image source={{ uri: GROOM_ICON }} style={styles.shareOptionIconSmall} />
                      </View>
                    </View>
                    <View style={styles.shareOptionLabelRow}>
                      <Text style={[styles.shareOptionLabel, isBothShared && styles.shareOptionLabelDone]}>Share with Both</Text>
                      {isBothShared && (
                        <View style={styles.sharedBadge}>
                          <SvgIcon name="checkmark-circle" size={16} color={colors.success} />
                          <Text style={styles.sharedBadgeText}>Shared</Text>
                        </View>
                      )}
                    </View>
                    <Text style={styles.shareOptionDesc}>Exchange profiles between Bride & Groom</Text>
                  </TouchableOpacity>
                </View>
              );
            })()}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Custom Dialog Modal */}
      <Modal visible={dialog.visible} transparent animationType="fade">
        <View style={styles.dialogOverlay}>
          <View style={styles.dialogContainer}>
            <View style={[styles.dialogHeader, dialog.type === 'success' ? styles.dialogHeaderSuccess : styles.dialogHeaderError]}>
              <SvgIcon 
                name={dialog.type === 'success' ? 'checkmark-circle' : 'alert-circle'} 
                size={50} 
                color="#fff" 
              />
            </View>
            <View style={styles.dialogBody}>
              <Text style={styles.dialogTitle}>{dialog.title}</Text>
              <Text style={styles.dialogMessage}>{dialog.message}</Text>
              <TouchableOpacity 
                style={[styles.dialogButton, dialog.type === 'success' ? styles.dialogButtonSuccess : styles.dialogButtonError]}
                onPress={handleDialogConfirm}
              >
                <Text style={styles.dialogButtonText}>OK</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: colors.textSecondary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.secondary,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  compactHeader: {
    backgroundColor: colors.secondary,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  backButton: {
    padding: 4,
  },
  iconButton: {
    padding: 4,
  },
  memberInfoCompact: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  memberAvatarSmall: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  memberInitialsSmall: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.white,
  },
  memberTextCompact: {
    flex: 1,
  },
  headerTitleCompact: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  memberNameSmall: {
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.85)',
    marginTop: 2,
  },
  searchInputCompact: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: colors.white,
  },
  closeButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.white,
    flex: 1,
    textAlign: 'center',
  },
  memberSection: {
    backgroundColor: colors.secondary,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  memberCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 12,
  },
  memberAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  memberInitials: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.white,
  },
  memberInfo: {
    flex: 1,
  },
  sharingToLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
  },
  memberDetails: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  searchSection: {
    backgroundColor: colors.gray800,
    padding: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 12,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  searchInput: {
    flex: 1,
    backgroundColor: colors.background,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 14,
    color: colors.white,
  },
  searchButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.secondary,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 6,
  },
  searchButtonText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '600',
  },
  resultsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: colors.gray800,
  },
  resultsText: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  selectedText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.secondary,
  },
  profilesList: {
    padding: 16,
  },
  profileCard: {
    backgroundColor: colors.gray800,
    borderRadius: 12,
    marginBottom: 16,
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  cardBody: {
    flexDirection: 'row',
    height: 160,
  },
  photoSection: {
    width: '50%',
    position: 'relative',
  },
  profileImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  shareIconButton: {
    position: 'absolute',
    top: 8,
    left: 8,
    zIndex: 10,
    backgroundColor: 'rgba(0, 169, 160, 0.9)',
    borderRadius: 20,
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  shareIconButtonShared: {
    backgroundColor: 'rgba(76, 175, 80, 0.9)',
  },
  shareIconButtonPartial: {
    backgroundColor: 'rgba(33, 150, 243, 0.9)',
  },
  shareIconButtonPending: {
    backgroundColor: 'rgba(255, 152, 0, 0.9)',
  },
  pendingBanner: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.warning,
    paddingVertical: 4,
    gap: 4,
  },
  pendingText: {
    fontSize: 10,
    fontWeight: '600',
    color: colors.white,
  },
  matchBadge: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: colors.secondary,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  matchText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.white,
  },
  shareResponseContainer: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    flexDirection: 'row',
    gap: 6,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 8,
    padding: 6,
  },
  shareIconContainer: {
    position: 'relative',
  },
  shareTypeIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 2,
    borderColor: colors.white,
  },
  responseIndicator: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 16,
    height: 16,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.white,
  },
  detailsSection: {
    width: '50%',
    padding: 12,
    justifyContent: 'space-between',
  },
  nameContainer: {
    marginBottom: 8,
  },
  profileName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    gap: 6,
  },
  infoText: {
    fontSize: 14,
    color: colors.textSecondary,
    flex: 1,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  emptyText: {
    fontSize: 16,
    color: colors.textSecondary,
    marginTop: 16,
  },
  shareButtonContainer: {
    padding: 16,
    backgroundColor: colors.gray800,
    borderTopWidth: 1,
    borderTopColor: colors.gray700,
  },
  shareButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.secondary,
    paddingVertical: 14,
    borderRadius: 8,
    gap: 8,
  },
  shareButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  dialogOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  dialogContainer: {
    backgroundColor: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    overflow: 'hidden',
  },
  dialogHeader: {
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialogHeaderSuccess: {
    backgroundColor: colors.success,
  },
  dialogHeaderError: {
    backgroundColor: colors.error,
  },
  dialogBody: {
    padding: 24,
    alignItems: 'center',
  },
  dialogTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 12,
    textAlign: 'center',
  },
  dialogMessage: {
    fontSize: 16,
    color: colors.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  dialogButton: {
    paddingVertical: 12,
    paddingHorizontal: 40,
    borderRadius: 8,
    minWidth: 120,
  },
  dialogButtonSuccess: {
    backgroundColor: colors.success,
  },
  dialogButtonError: {
    backgroundColor: colors.error,
  },
  dialogButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  // Share Options Modal Styles
  shareOptionsOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  shareOptionsContainer: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingBottom: 30,
  },
  shareOptionsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  shareOptionsTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.primary,
  },
  closeButton: {
    padding: 4,
  },
  shareOptionsBody: {
    padding: 20,
    gap: 16,
  },
  shareOptionItem: {
    backgroundColor: colors.gray100 || '#f5f5f5',
    borderRadius: 16,
    padding: 20,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  shareOptionItemBoth: {
    backgroundColor: colors.secondary,
    borderColor: colors.secondary,
  },
  shareOptionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  shareOptionIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#fff',
  },
  shareOptionIconSmall: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#fff',
  },
  bothIconsContainer: {
    flexDirection: 'row',
    gap: 4,
  },
  arrowIcon: {
    marginHorizontal: 12,
  },
  shareOptionLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.primary,
    marginBottom: 4,
  },
  shareOptionLabelBoth: {
    color: '#fff',
  },
  shareOptionLabelDone: {
    color: colors.success,
  },
  shareOptionLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 4,
  },
  shareOptionDesc: {
    fontSize: 13,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  shareOptionDescBoth: {
    color: 'rgba(255,255,255,0.8)',
  },
  shareOptionItemDone: {
    backgroundColor: '#E8F5E9',
    borderColor: colors.success,
    borderWidth: 2,
  },
  shareOptionItemBothDone: {
    backgroundColor: '#E8F5E9',
    borderColor: colors.success,
    borderWidth: 2,
  },
  sharedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  sharedBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.success,
  },
});

export default ShareProfileScreen;
